import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const ReloadIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const CacheIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <ellipse cx="12" cy="5" rx="9" ry="3" />
    <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
  </svg>
);

const LogoutIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const TrashIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);
import { useQueryClient } from "@tanstack/react-query";
import { clearQueryCache, storage } from "utils/storage.ts";
import AISettings from "./AISettings.tsx";
import PWAStatus from "../camera/PWAStatus.tsx";
import "./SettingsPage.css";
import SettingsSection from "./components/SettingsSection.tsx";
import SettingsRow from "./components/SettingsRow.tsx";
import SettingsActions from "./components/SettingsActions.tsx";
import SettingButton from "./components/SettingButton.tsx";

interface BuildInfo {
  deployedAt: string;
  source?: string;
}

const formatDeployTimestamp = (deployedAt: string) => {
  const deployedDate = new Date(deployedAt);

  if (Number.isNaN(deployedDate.getTime())) {
    return null;
  }

  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
  }).format(deployedDate);
};

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const config = storage.getConfig();
  const [buildInfo, setBuildInfo] = useState<BuildInfo | null>(null);
  const [buildInfoLoaded, setBuildInfoLoaded] = useState(false);

  useEffect(() => {
    let isMounted = true;

    fetch("/build-info.json", { cache: "no-store" })
      .then((response) => {
        if (!response.ok) return null;
        return response.json() as Promise<BuildInfo>;
      })
      .then((data) => {
        if (!isMounted) return;
        setBuildInfo(data?.deployedAt ? data : null);
      })
      .catch(() => {
        if (isMounted) setBuildInfo(null);
      })
      .finally(() => {
        if (isMounted) setBuildInfoLoaded(true);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const timezoneName = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || "Unknown",
    [],
  );
  const deployedAtText = buildInfo?.deployedAt
    ? formatDeployTimestamp(buildInfo.deployedAt)
    : null;

  const handleLogout = () => {
    storage.clearConfig();
    navigate("/login");
  };

  const handleClearData = () => {
    if (
      confirm(
        "Are you sure you want to clear all stored data? This will log you out.",
      )
    ) {
      storage.clearConfig();
      navigate("/login");
    }
  };

  const handleClearCache = () => {
    // Clear both localStorage cache and in-memory cache
    clearQueryCache();
    queryClient.clear();
    globalThis.location.reload();
  };

  const handleForceReload = () => {
    globalThis.location.reload();
  };

  return (
    <div className="settings-page">
      <div className="settings-page__container">
        <h1 className="settings-page__title">Settings</h1>

        <div className="settings-page__content">
          <SettingsSection title="Configuration">
            <SettingsRow label="Base URL:">{config?.baseUrl}</SettingsRow>
            <SettingsRow label="API Key:">
              {config?.apiKey
                ? `${config.apiKey.substring(0, 8)}...`
                : "Not set"}
            </SettingsRow>
          </SettingsSection>

          <SettingsSection title="Account">
            <SettingsActions>
              <SettingButton onClick={handleForceReload} icon={<ReloadIcon />}>
                Force Reload
              </SettingButton>
              <SettingButton onClick={handleClearCache} icon={<CacheIcon />}>
                Clear Cached Data
              </SettingButton>
              <SettingButton
                onClick={handleLogout}
                variant="warning"
                icon={<LogoutIcon />}
              >
                Logout
              </SettingButton>
              <SettingButton
                onClick={handleClearData}
                variant="danger"
                icon={<TrashIcon />}
              >
                Clear All Data
              </SettingButton>
            </SettingsActions>
          </SettingsSection>

          <AISettings />

          <SettingsSection
            title="PWA Status"
            containerClassName="settings-page__pwa-info"
          >
            <PWAStatus className="settings-page__pwa-status" />
          </SettingsSection>

          <SettingsSection
            title="About"
            containerClassName="settings-page__about"
          >
            <p className="settings-page__about-text">Grocy Tool v1.0.1</p>
            <p className="settings-page__about-text">
              A mobile-friendly interface for managing your Grocy shopping list.
            </p>
            <div className="settings-page__deploy-info">
              <div>
                <span className="settings-page__deploy-label">Deployed at</span>
                <span className="settings-page__deploy-value">
                  {buildInfoLoaded
                    ? deployedAtText ?? "Not available"
                    : "Loading..."}
                </span>
              </div>
              <div>
                <span className="settings-page__deploy-label">Timezone</span>
                <span className="settings-page__deploy-value">
                  {timezoneName}
                </span>
              </div>
            </div>
          </SettingsSection>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
